<script setup>
import { computed, getCurrentInstance, onMounted, ref } from 'vue'
import { DriverStore } from '../pinia/Driver.js'

const Driver = DriverStore()
const { proxy } = getCurrentInstance()
const saved = ref(false)
const copied = ref(false)
const error = ref('')
const loading = ref(true)
const saving = ref(false)
const challenges = ref([])
const selectedChallenge = ref(null)
const showMilestones = ref(true)

const difficultyLabels = { 0: 'Very Easy', 1: 'Easy', 2: 'Medium', 3: 'Hard', 4: 'Very Hard' }
const personaName = computed(() => Driver.persona?.name || '')
const overlayUrl = computed(() =>
    personaName.value ? `https://nightriderz.world/overlay/challenge/${encodeURIComponent(personaName.value)}` : ''
)
const challengeOptions = computed(() => challenges.value.map((challenge) => {
    const race = (challenge.conditions || []).find((condition) => condition.type === 'raceID')
    const name = String(race?.race_name || 'Challenge').trim()
    const raceClass = challenge.class || race?.race_class || '?'
    const difficulty = difficultyLabels[challenge.difficulty] || 'Medium'
    return { value: Number(challenge.ID), title: `[${raceClass}] ${name} · ${difficulty}` }
}))

async function load() {
    loading.value = true
    error.value = ''
    try {
        const formatter = new Intl.DateTimeFormat('en-GB', {
            timeZone: 'Europe/Paris',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
        })
        const parts = Object.fromEntries(formatter.formatToParts(new Date()).map((part) => [part.type, part.value]))
        const date = new Date(`${parts.year}-${parts.month}-${parts.day}T12:00:00Z`)
        const year = date.getUTCFullYear()
        const day = date.getUTCDay() || 7
        date.setUTCDate(date.getUTCDate() + 4 - day)
        const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1))
        const week = Math.ceil((((date - yearStart) / 86400000) + 1) / 7)
        const result = await proxy.$api({
            serviceName: 'challenges',
            methodName: 'GetWeek',
            parameters: [year, week],
        })
        challenges.value = Array.isArray(result?.loop) ? result.loop : []

        const savedChallenge = Driver.user?.overlay_challenge_id
        const savedMilestones = Driver.user?.overlay_show_milestones
        showMilestones.value = savedMilestones == null ? true : Number(savedMilestones) === 1
        if (savedChallenge != null && challenges.value.some((challenge) => Number(challenge.ID) === Number(savedChallenge))) {
            selectedChallenge.value = Number(savedChallenge)
        } else {
            selectedChallenge.value = challenges.value.length ? Number(challenges.value[0].ID) : null
        }
    } catch (exception) {
        error.value = exception?.response?.data?.error || "Could not load this week's challenges."
        challenges.value = []
    } finally {
        loading.value = false
    }
}

async function save() {
    if (!personaName.value) {
        error.value = 'Select a persona first.'
        return
    }

    saving.value = true
    error.value = ''
    try {
        const result = await proxy.$api({
            serviceName: 'account',
            methodName: 'UpdateOverlayPrefs',
            parameters: [selectedChallenge.value, showMilestones.value ? 1 : 0],
        })
        if (result?.error) {
            error.value = result.error
            return
        }
        Driver.user.overlay_challenge_id = result?.overlay_challenge_id ?? selectedChallenge.value
        Driver.user.overlay_show_milestones = result?.overlay_show_milestones ?? (showMilestones.value ? 1 : 0)
        saved.value = true
    } catch (exception) {
        error.value = exception?.response?.data?.error || 'Could not save overlay settings.'
    } finally {
        saving.value = false
    }
}

async function copyUrl() {
    if (!overlayUrl.value) return
    try {
        await navigator.clipboard.writeText(overlayUrl.value)
        copied.value = true
    } catch {
        error.value = 'Could not copy link. Select and copy it manually.'
    }
}

onMounted(load)
</script>

<template><div class="settings-tab-page stream-overlay-form">
        <v-snackbar v-model="saved" :timeout="2000" color="success" elevation="24">Overlay settings saved.</v-snackbar>
        <v-snackbar v-model="copied" :timeout="2000" color="success" elevation="24">OBS link copied.</v-snackbar>

        <div class="settings-tab-header">
            <div class="header-icon"><v-icon size="28">mdi-twitch</v-icon></div>
            <div class="header-content">
                <h3 class="page-title">Streamer overlay</h3>
                <p class="page-subtitle">OBS browser source for a weekly challenge</p>
            </div>
        </div>

        <v-alert v-if="error" class="mb-4" type="error" :text="error" />
        <div v-if="loading" class="loading-state">
            <v-progress-circular indeterminate color="primary" size="28" />
            <span>Loading challenges…</span>
        </div>
        <template v-else>
            <div class="section-list">
                <div class="section-row">
                    <div class="section-body">
                        <div class="setting-label">
                            <v-icon size="20" class="mr-2">mdi-trophy-award</v-icon>
                            Tracked challenge
                        </div>
                        <p class="setting-description">
                            Pick which challenge this week appears on your overlay. Updates when you save.
                        </p>
                        <v-select v-model="selectedChallenge" :items="challengeOptions" item-title="title" item-value="value" variant="outlined" density="comfortable" hide-details class="mt-3" :disabled="!challengeOptions.length" placeholder="No challenges this week" />
                    </div>
                </div>
                <div class="section-row">
                    <div class="section-body setting-item">
                        <div class="setting-info">
                            <div class="setting-label">
                                <v-icon size="20" class="mr-2">mdi-flag-checkered</v-icon>
                                Show milestones
                            </div>
                            <p class="setting-description">
                                Display weekly milestone progress under the challenge card.
                            </p>
                        </div>
                        <v-switch v-model="showMilestones" color="primary" hide-details density="comfortable" class="setting-switch" />
                    </div>
                </div>
                <div class="section-row">
                    <div class="section-body">
                        <div class="setting-label">
                            <v-icon size="20" class="mr-2">mdi-link</v-icon>
                            OBS browser source URL
                        </div>
                        <p class="setting-description">
                            Add a Browser Source in OBS. Use a transparent background. Suggested size <strong>480×720</strong>.
                        </p>
                        <div class="url-row">
                            <input class="url-input" type="text" readonly :value="overlayUrl">
                            <v-btn color="primary" variant="tonal" :disabled="!overlayUrl" prepend-icon="mdi-content-copy" @click="copyUrl">Copy</v-btn>
                        </div>
                        <p v-if="!personaName" class="setting-description warn">Log in with a persona to generate your link.</p>
                    </div>
                </div>
            </div>
            <v-btn class="save-btn mt-4" variant="elevated" color="primary" size="large" block :loading="saving" :disabled="!challengeOptions.length" @click="save">
                <v-icon class="mr-2">mdi-content-save</v-icon>
                Save overlay settings
            </v-btn>
        </template>
    </div></template>

<style scoped>
.loading-state {
  display:flex;
  align-items:center;
  gap:12px;
  color:var(--mute);
  padding:24px 0}
.setting-item {
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:16px}
.setting-info {
  min-width:0;
  flex:1}
.setting-label {
  display:flex;
  align-items:center;
  font-weight:600;
  color:var(--ink);
  margin-bottom:4px}
.setting-description {
  margin:0;
  color:var(--mute);
  font-size:.88rem;
  line-height:1.4}
.setting-description.warn {
  color:#ef9a9a;
  margin-top:8px}
.url-row {
  display:flex;
  gap:10px;
  margin-top:12px;
  align-items:center}
.url-input {
  flex:1;
  min-width:0;
  padding:10px 12px;
  border-radius:8px;
  border:1px solid var(--line);
  background:var(--glass);
  color:var(--ink);
  font-size:.82rem;
  font-family:ui-monospace,SFMono-Regular,Menlo,Consolas,monospace}
.save-btn {
  margin-top:8px}
</style>
